import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';

import {
  ElementHorizontalAlignment,
  ElementVerticalAlignment,
  LabelIconAlignment,
  LabelPosition,
  makeFontStyleStyles,
  makeLabelHorizontalAlignStyles,
  makeLabelVerticalAlignmentStyles,
  PropertiesConfigInputFontStyleOptions,
} from '@blaze-shared/elements-domain';
import { TooltipDirective } from '@blaze-shared/shared-ui';
import { getElementTooltipIconColor } from '@blaze-shared/shared-util';
import { FontsService, IconsModule } from '@blaze-shared/theming-data-access';

@Component({
  selector: 'blaze-element-label',
  imports: [MatIcon, NgStyle, IconsModule, TooltipDirective],
  templateUrl: './element-label.component.html',
  styleUrl: './element-label.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style]': 'computedHostStyles()',
  },
})
export class ElementLabelComponent {
  private readonly fontsService = inject(FontsService);

  width = input<string | undefined>();
  color = input<string | undefined>('');
  horizontalAlign = input<ElementHorizontalAlignment | undefined>('left');
  verticalAlign = input<ElementVerticalAlignment | undefined>('middle');
  fontFamily = input<string | undefined>('var(--bat-fontFamily)');
  fontSize = input<string | undefined>('var(--bat-fontSize)');
  fontStyle = input<PropertiesConfigInputFontStyleOptions | undefined>([]);
  fontWeight = input<string | undefined>('500');
  position = input<LabelPosition | undefined>('left');
  lineHeight = input<string | undefined>('var(--bat-lineHeight)');

  icon = input<string | undefined>();
  iconPosition = input<LabelIconAlignment | undefined>('left');
  iconColor = input<string | undefined>('');
  iconSize = input<string | undefined>('16px');
  tooltip = input<string | undefined>();

  isUsingOldPaddingStrategy = input<boolean | undefined>(false);
  extendInputPadding = input<boolean | undefined>(false);
  minHeight = input<string | undefined>('40px');

  labelStyles = computed(() => {
    const styles: Record<string, string | undefined> = {
      ...makeLabelHorizontalAlignStyles(this.horizontalAlign() ?? undefined),
      color: this.color(),
      fontFamily: this.fontFamily(),
      fontSize: this.fontSize(),
      fontWeight: this.fontsService.getFontWeight(this.fontFamily(), this.fontWeight()),
      lineHeight: this.lineHeight(),
    };

    if (this.position() === 'top') {
      styles.width = '100%';

      if (this.isUsingOldPaddingStrategy() || this.extendInputPadding()) {
        styles.paddingTop = '8px';
      }
    } else {
      styles.minHeight = this.minHeight();
    }

    return styles;
  });

  tooltipColor = computed(() => getElementTooltipIconColor(this.color() ?? '#000'));
  isIconPrefix = computed(() => this.iconPosition() !== 'right');

  textStyles = computed(() => {
    return {
      ...makeFontStyleStyles(this.fontStyle() ?? []),
    };
  });

  iconStyles = computed(() => {
    return {
      '--icon-size': this.iconSize(),
      color: this.iconColor(),
    };
  });

  computedHostStyles = computed(() => {
    const hostStyles: Record<string, string | undefined> = {
      ...makeLabelVerticalAlignmentStyles(this.verticalAlign()),
    };

    if (this.position() === 'top') {
      hostStyles.width = '100%';
    } else {
      hostStyles.flexBasis = this.width();
    }

    if (this.width() === 'auto') {
      hostStyles.flexShrink = '0';
      hostStyles.maxWidth = '80%';
    }

    return hostStyles;
  });
}
